import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Typography, Grid, Card, CardContent, Box, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Avatar, LinearProgress,
} from '@mui/material';
import {
  Groups, TrendingUp, EmojiEvents, Person,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Legend,
} from 'recharts';
import { startOfWeek, endOfWeek, subWeeks, format } from 'date-fns';
import { useNotification } from '../context_providers/NotificationContext';
import { User } from '../../types';
import apiService from '../../utils/api.service';
import LoadingSpinner from '../shared/LoadingSpinner';

// ==================== Constants ====================
const CHART_COLORS = {
  primary: '#7b6df6',
  secondary: '#10b981',
  blue: '#3b82f6',
  amber: '#f59e0b',
  rose: '#f43f5e',
  nonBillable: '#334155',
};

const DEFAULT_WEEKLY_CAPACITY = 40;

interface TeamMemberData {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl?: string;
  orgRole: string;
  status: string;
  weeklyCapacity: number;
  thisWeekHours: number;
  thisWeekBillable: number;
  thisWeekNonBillable: number;
  lastWeekHours: number;
  avgWeeklyHours: number;
  billablePercent: number;
}

interface TeamStats {
  totalMembers: number;
  avgWeeklyHours: number;
  topPerformer: string;
  totalThisWeek: number;
}

// ==================== Helpers ====================
const getCapacityColor = (percent: number): 'success' | 'warning' | 'error' => {
  if (percent >= 80) return 'success';
  if (percent >= 60) return 'warning';
  return 'error';
};

const getCapacityLabel = (percent: number): string => {
  if (percent >= 80) return 'On Track';
  if (percent >= 60) return 'Moderate';
  return 'Low';
};

const getInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

// ==================== Custom Tooltip ====================
const ChartTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
      borderRadius: 1.5, px: 2, py: 1, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{label}</Typography>
      {payload.map((p: any, i: number) => (
        <Typography key={i} variant="body2" fontWeight={600} sx={{ color: p.color || p.fill }}>
          {p.name}: {p.value?.toFixed(1)}h
        </Typography>
      ))}
    </Box>
  );
};

// ==================== Stat Card ====================
const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subtitle?: string;
  color: string;
}> = ({ icon, label, value, subtitle, color }) => (
  <Card sx={{
    background: `linear-gradient(135deg, ${color}20, ${color}08)`,
    border: '1px solid rgba(148, 163, 184, 0.08)',
    transition: 'all 0.3s ease',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' },
  }}>
    <CardContent sx={{ py: 3 }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: '12px', bgcolor: `${color}25`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        mb: 2, color,
      }}>
        {icon}
      </Box>
      <Typography variant="h3" fontWeight={700} sx={{ mb: 0.5 }}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {subtitle && (
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

// ==================== Main Component ====================
const TeamPage: React.FC = () => {
  const { showError } = useNotification();
  const [members, setMembers] = useState<TeamMemberData[]>([]);
  const [stats, setStats] = useState<TeamStats>({
    totalMembers: 0,
    avgWeeklyHours: 0,
    topPerformer: '-',
    totalThisWeek: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  const today = useMemo(() => new Date(), []);
  const thisWeekStart = useMemo(() => startOfWeek(today, { weekStartsOn: 1 }), [today]);
  const thisWeekEnd = useMemo(() => endOfWeek(today, { weekStartsOn: 1 }), [today]);
  const lastWeekStart = useMemo(() => startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }), [today]);
  const lastWeekEnd = useMemo(() => endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }), [today]);

  useEffect(() => {
    const fetchTeamData = async () => {
      setIsLoading(true);
      try {
        const [membersResp, thisWeekResp, lastWeekResp] = await Promise.allSettled([
          apiService.listOrgMembers(),
          apiService.getReportByUser({
            start_date: format(thisWeekStart, 'yyyy-MM-dd'),
            end_date: format(thisWeekEnd, 'yyyy-MM-dd'),
          }),
          apiService.getReportByUser({
            start_date: format(lastWeekStart, 'yyyy-MM-dd'),
            end_date: format(lastWeekEnd, 'yyyy-MM-dd'),
          }),
        ]);

        // Parse members
        const rawMembersData = membersResp.status === 'fulfilled'
          ? (membersResp.value?.data || membersResp.value || [])
          : [];
        const rawMembers: User[] = Array.isArray(rawMembersData) ? rawMembersData : (rawMembersData?.members || []);

        // Parse weekly reports into lookup maps
        const thisWeekRaw = thisWeekResp.status === 'fulfilled'
          ? (thisWeekResp.value?.data || thisWeekResp.value || [])
          : [];
        const thisWeekData: any[] = Array.isArray(thisWeekRaw) ? thisWeekRaw : (thisWeekRaw?.users || thisWeekRaw?.data || []);
        const lastWeekRaw = lastWeekResp.status === 'fulfilled'
          ? (lastWeekResp.value?.data || lastWeekResp.value || [])
          : [];
        const lastWeekData: any[] = Array.isArray(lastWeekRaw) ? lastWeekRaw : (lastWeekRaw?.users || lastWeekRaw?.data || []);
        const thisWeekMap = new Map<string, any>();
        (Array.isArray(thisWeekData) ? thisWeekData : []).forEach((item: any) => {
          thisWeekMap.set(item.userId || item.id, item);
        });

        const lastWeekMap = new Map<string, any>();
        (Array.isArray(lastWeekData) ? lastWeekData : []).forEach((item: any) => {
          lastWeekMap.set(item.userId || item.id, item);
        });

        // Build team member data
        const activeMembers = (Array.isArray(rawMembers) ? rawMembers : [])
          .filter((m) => m.status === 'active' || m.isActive);

        const teamMembers: TeamMemberData[] = activeMembers.map((m) => {
          const tw = thisWeekMap.get(m.id) || {};
          const lw = lastWeekMap.get(m.id) || {};
          const thisWeekHours = tw.totalHours || tw.hours || 0;
          const thisWeekBillable = tw.billableHours || 0;
          const lastWeekHours = lw.totalHours || lw.hours || 0;
          const avgWeeklyHours = (thisWeekHours + lastWeekHours) / 2;
          const billablePercent = thisWeekHours > 0
            ? (thisWeekBillable / thisWeekHours) * 100
            : 0;

          return {
            id: m.id,
            name: `${m.firstName} ${m.lastName}`,
            firstName: m.firstName,
            lastName: m.lastName,
            email: m.email,
            avatarUrl: m.avatarUrl,
            orgRole: m.orgRole,
            status: m.status,
            weeklyCapacity: m.weeklyCapacity || DEFAULT_WEEKLY_CAPACITY,
            thisWeekHours,
            thisWeekBillable,
            thisWeekNonBillable: Math.max(0, thisWeekHours - thisWeekBillable),
            lastWeekHours,
            avgWeeklyHours: Math.round(avgWeeklyHours * 10) / 10,
            billablePercent: Math.round(billablePercent),
          };
        });

        // Sort by this week hours descending
        teamMembers.sort((a, b) => b.thisWeekHours - a.thisWeekHours);
        setMembers(teamMembers);

        // Compute summary stats
        const totalThisWeek = teamMembers.reduce((s, m) => s + m.thisWeekHours, 0);
        const avgHours = teamMembers.length > 0
          ? totalThisWeek / teamMembers.length
          : 0;
        const topPerformer = teamMembers.length > 0
          ? teamMembers[0].name
          : '-';

        setStats({
          totalMembers: teamMembers.length,
          avgWeeklyHours: Math.round(avgHours * 10) / 10,
          topPerformer,
          totalThisWeek: Math.round(totalThisWeek * 10) / 10,
        });
      } catch (err: any) {
        showError(err.message || 'Failed to load team data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTeamData();
  }, [showError, thisWeekStart, thisWeekEnd, lastWeekStart, lastWeekEnd]);

  if (isLoading) return <LoadingSpinner message="Loading team data..." />;

  // Chart data: top 15 members for the stacked bar
  const chartData = members.slice(0, 15).map((m) => ({
    name: m.firstName.length > 10
      ? `${m.firstName.substring(0, 10)}...`
      : m.firstName,
    fullName: m.name,
    billable: m.thisWeekBillable,
    nonBillable: m.thisWeekNonBillable,
    total: m.thisWeekHours,
  }));

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700}>Team Overview</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
          Week of {format(thisWeekStart, 'MMM d')} - {format(thisWeekEnd, 'MMM d, yyyy')}
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<Groups sx={{ fontSize: 24 }} />}
            label="Total Members"
            value={stats.totalMembers}
            color={CHART_COLORS.primary}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TrendingUp sx={{ fontSize: 24 }} />}
            label="Avg Hours/Week"
            value={`${stats.avgWeeklyHours}h`}
            subtitle="Per active member"
            color={CHART_COLORS.secondary}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<EmojiEvents sx={{ fontSize: 24 }} />}
            label="Top Performer"
            value={stats.topPerformer}
            subtitle="Most hours this week"
            color={CHART_COLORS.amber}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TrendingUp sx={{ fontSize: 24 }} />}
            label="Team Total"
            value={`${stats.totalThisWeek}h`}
            subtitle="This week"
            color={CHART_COLORS.blue}
          />
        </Grid>
      </Grid>

      {/* Team Hours Chart */}
      {chartData.length > 0 && (
        <Card sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Team Hours This Week
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Billable vs non-billable hours per member
          </Typography>
          <ResponsiveContainer width="100%" height={Math.max(280, chartData.length * 44)}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 4, right: 40, left: 0, bottom: 4 }}
            >
              <XAxis
                type="number"
                tick={{ fill: '#94a3b8', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${v}h`}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fill: '#f1f5f9', fontSize: 13 }}
                width={110}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(123, 109, 246, 0.06)' }} />
              <Legend
                verticalAlign="top"
                align="right"
                wrapperStyle={{ fontSize: 13 }}
                formatter={(value: string) => (
                  <span style={{ color: '#94a3b8' }}>{value}</span>
                )}
              />
              <Bar
                dataKey="billable"
                name="Billable"
                stackId="hours"
                fill={CHART_COLORS.primary}
                radius={[0, 0, 0, 0]}
                maxBarSize={28}
              />
              <Bar
                dataKey="nonBillable"
                name="Non-Billable"
                stackId="hours"
                fill={CHART_COLORS.nonBillable}
                radius={[0, 6, 6, 0]}
                maxBarSize={28}
              />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Team Utilization Table */}
      <Card sx={{ overflow: 'hidden' }}>
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Team Utilization
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Detailed breakdown of hours and capacity for each team member
          </Typography>
        </Box>
        {members.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Person sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No team members found
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Invite team members to start tracking utilization.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ mt: 1 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Member</TableCell>
                  <TableCell align="right">This Week</TableCell>
                  <TableCell align="right">Last Week</TableCell>
                  <TableCell align="right">Avg Weekly</TableCell>
                  <TableCell align="right">Billable %</TableCell>
                  <TableCell align="center">Capacity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member) => {
                  const capacityPercent = member.weeklyCapacity > 0
                    ? (member.thisWeekHours / member.weeklyCapacity) * 100
                    : 0;
                  const cappedPercent = Math.min(100, capacityPercent);
                  const capacityColor = getCapacityColor(cappedPercent);
                  const capacityLabel = getCapacityLabel(cappedPercent);

                  return (
                    <TableRow
                      key={member.id}
                      sx={{ '&:hover': { bgcolor: 'rgba(123, 109, 246, 0.04)' } }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            src={member.avatarUrl}
                            sx={{
                              width: 36, height: 36,
                              bgcolor: CHART_COLORS.primary + '30',
                              color: CHART_COLORS.primary,
                              fontSize: 14, fontWeight: 600,
                            }}
                          >
                            {getInitials(member.firstName, member.lastName)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {member.name}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              {member.orgRole}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {member.thisWeekHours.toFixed(1)}h
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            {member.lastWeekHours.toFixed(1)}h
                          </Typography>
                          {member.thisWeekHours !== 0 && member.lastWeekHours !== 0 && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: member.thisWeekHours >= member.lastWeekHours
                                  ? CHART_COLORS.secondary
                                  : CHART_COLORS.rose,
                              }}
                            >
                              {member.thisWeekHours >= member.lastWeekHours ? '+' : ''}
                              {((member.thisWeekHours - member.lastWeekHours) / (member.lastWeekHours || 1) * 100).toFixed(0)}%
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {member.avgWeeklyHours.toFixed(1)}h
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, member.billablePercent)}
                            sx={{
                              width: 50, height: 6, borderRadius: 3,
                              bgcolor: 'rgba(148,163,184,0.1)',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: member.billablePercent >= 70
                                  ? CHART_COLORS.secondary
                                  : member.billablePercent >= 40
                                    ? CHART_COLORS.amber
                                    : CHART_COLORS.rose,
                                borderRadius: 3,
                              },
                            }}
                          />
                          <Typography variant="body2" sx={{ minWidth: 32 }}>
                            {member.billablePercent}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            label={capacityLabel}
                            size="small"
                            color={capacityColor}
                            variant="outlined"
                            sx={{ fontSize: 11, height: 22 }}
                          />
                          <Typography variant="caption" color="text.disabled">
                            {cappedPercent.toFixed(0)}% of {member.weeklyCapacity}h
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Container>
  );
};

export default TeamPage;
